import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { CardCoverageStatus } from '@sorare/core/src/__generated__/globalTypes';
import { Ineligible } from '@sorare/core/src/atoms/icons/Ineligible';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelS, LabelXS } from '@sorare/core/src/atoms/typography';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { messages } from '@sorare/core/src/lib/cardCoverage';
import { isType } from '@sorare/core/src/lib/gql';
import type { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';
import { BasicBenchExtraWrapper } from '@sorare/marketplace/src/components/searchPlayerTable/BasicBenchExtraWrapper';

import { BenchExtraTooltip } from '#src/components/compose/BenchExtraTooltip/index.tsx';
import {
  CardUsageStrip,
  getCardUsage,
} from '#src/components/compose/CardUsageStrip/index.tsx';

import { BenchExtraInjury } from './BenchExtraInjury/index.tsx';
import { BenchExtraSuspension } from './BenchExtraSuspension/index.tsx';
import type {
  BenchExtra_anyCardInterface,
  BenchExtra_anyPlayerInterface,
  BenchExtra_suspension,
} from './__generated__/index.graphql.ts';

const EmptyIcon = styled.div`
  width: var(--intermediate-unit);
  height: var(--intermediate-unit);
`;

type Props = {
  anyCard: BenchExtra_anyCardInterface | undefined;
  anyPlayer: BenchExtra_anyPlayerInterface;
  suspensions: BenchExtra_suspension[];
  hasMultipleGames?: boolean;
  so5LineupId?: string;
  taskId?: string;
  iconOnly?: boolean;
  price?: MonetaryAmount | null;
  defaultNode?: ReactNode;
  showInjury?: boolean;
  showSuspension?: boolean;
};

export const BenchExtra = ({
  so5LineupId,
  taskId,
  anyCard,
  anyPlayer,
  suspensions,
  hasMultipleGames,
  iconOnly,
  price,
  showInjury = true,
  showSuspension = true,
}: Props) => {
  const { main, exponent } = useAmountWithConversion({
    monetaryAmount: price,
  });
  const usage = getCardUsage(anyCard?.usedIn, { so5LineupId, taskId });

  const [injury] = anyPlayer.activeInjuries;
  const [suspension] = suspensions;

  let content: ReactNode = null;
  if (usage) {
    content = <CardUsageStrip usage={usage} iconOnly={iconOnly} />;
  } else if (price) {
    return (
      <BasicBenchExtraWrapper>
        <Horizontal gap={0.5}>
          <LabelS bold>{main}</LabelS>
          {!!exponent && <LabelXS color="var(--c-nd-600)">{exponent}</LabelXS>}
        </Horizontal>
      </BasicBenchExtraWrapper>
    );
  } else if (
    isType(anyCard, 'Card') &&
    [
      CardCoverageStatus.NOT_COVERED,
      CardCoverageStatus.NOT_ELIGIBLE,
      CardCoverageStatus.SPECIAL,
    ].includesValue(anyCard.coverageStatus)
  ) {
    content = (
      <Horizontal gap={0.5} center>
        <Ineligible size={1.5} />
        {!iconOnly && (
          <LabelXS>
            <FormattedMessage
              {...messages[
                anyCard.coverageStatus as
                  | typeof CardCoverageStatus.NOT_COVERED
                  | typeof CardCoverageStatus.NOT_ELIGIBLE
                  | typeof CardCoverageStatus.SPECIAL
              ]}
            />
          </LabelXS>
        )}
      </Horizontal>
    );
  } else if (injury && showInjury) {
    content = <BenchExtraInjury anyPlayer={anyPlayer} iconOnly={iconOnly} />;
  } else if (suspension && showSuspension) {
    content = <BenchExtraSuspension iconOnly={iconOnly} />;
  } else if (hasMultipleGames && !iconOnly) {
    content = (
      <LabelXS>
        <FormattedMessage defaultMessage="Best score chosen" />
      </LabelXS>
    );
  }
  if (content) {
    return (
      <ConditionalWrapper wrap={!iconOnly} Wrapper={BasicBenchExtraWrapper}>
        <BenchExtraTooltip
          so5LineupId={so5LineupId}
          anyCard={anyCard}
          anyPlayer={anyPlayer}
          suspensions={suspensions}
          showInjury={showInjury}
          showSuspension={showSuspension}
        >
          {content}
        </BenchExtraTooltip>
      </ConditionalWrapper>
    );
  }

  if (iconOnly) {
    return <EmptyIcon />;
  }

  return null;
};

BenchExtra.fragments = {
  anyCard: gql`
    fragment BenchExtra_anyCardInterface on AnyCardInterface {
      slug
      usedIn(
        so5LeaderboardSlug: $so5LeaderboardSlug
        taskId: $taskId
        gameId: $gameId
      ) {
        slug
        ...getCardUsage_cardUsageInterface
      }
      ... on Card {
        slug
        coverageStatus
      }
      ...BenchExtraTooltip_anyCardInterface
    }
    ${BenchExtraTooltip.fragments.anyCard}
    ${getCardUsage.fragments.cardUsageInterface}
  ` as TypedDocumentNode<BenchExtra_anyCardInterface>,
  anyPlayer: gql`
    fragment BenchExtra_anyPlayerInterface on AnyPlayerInterface {
      slug
      activeInjuries {
        id
      }
      ...BenchExtraInjury_anyPlayerInterface
      ...BenchExtraTooltip_anyPlayerInterface
    }
    ${BenchExtraTooltip.fragments.anyPlayer}
    ${BenchExtraInjury.fragments.anyPlayer}
  ` as TypedDocumentNode<BenchExtra_anyPlayerInterface>,
  suspension: gql`
    fragment BenchExtra_suspension on Suspension {
      id
      ...BenchExtraTooltip_suspension
    }
    ${BenchExtraTooltip.fragments.suspension}
  ` as TypedDocumentNode<BenchExtra_suspension>,
};
